import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import {
  Popover, PopoverContent, PopoverTrigger,
} from './ui/popover';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from './ui/table';
import {
  AlertTriangle, FileSpreadsheet, FileText, Send, MessageCircle,
  Phone, Loader2, X, ExternalLink,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { dashboardAPI } from '../lib/api';

const WINDOW_META = {
  expired: {
    title: 'Already Expired',
    color: 'text-red-600',
    accent: 'border-red-300 bg-red-50',
    template: (r) => `Dear ${r.owner_name || 'Customer'}, your CNG inspection certificate (${r.verification_number || 'N/A'}) for ${r.registration_number || r.storage_serial_number || 'your vehicle/facility'} has EXPIRED on ${formatDate(r.valid_to)}. Please arrange for immediate recertification. — Shields Engineering`,
  },
  '7': {
    title: 'Expiring Within 7 Days',
    color: 'text-red-600',
    accent: 'border-red-300 bg-red-50',
    template: (r) => `Dear ${r.owner_name || 'Customer'}, your CNG inspection certificate (${r.verification_number || 'N/A'}) for ${r.registration_number || r.storage_serial_number || 'your vehicle/facility'} will expire on ${formatDate(r.valid_to)} (within 7 days). Please schedule recertification urgently. — Shields Engineering`,
  },
  '14': {
    title: 'Expiring Within 14 Days',
    color: 'text-amber-600',
    accent: 'border-amber-300 bg-amber-50',
    template: (r) => `Dear ${r.owner_name || 'Customer'}, your CNG inspection certificate (${r.verification_number || 'N/A'}) for ${r.registration_number || r.storage_serial_number || 'your vehicle/facility'} will expire on ${formatDate(r.valid_to)} (within 14 days). Please plan for recertification. — Shields Engineering`,
  },
  '30': {
    title: 'Expiring Within 30 Days',
    color: 'text-yellow-600',
    accent: 'border-yellow-300 bg-yellow-50',
    template: (r) => `Dear ${r.owner_name || 'Customer'}, this is a reminder that your CNG inspection certificate (${r.verification_number || 'N/A'}) for ${r.registration_number || r.storage_serial_number || 'your vehicle/facility'} expires on ${formatDate(r.valid_to)} (within 30 days). Please schedule recertification. — Shields Engineering`,
  },
};

function formatDate(v) {
  if (!v) return '—';
  try { return new Date(v).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return String(v).slice(0, 10); }
}

function normalizePhone(raw) {
  if (!raw) return '';
  const digits = String(raw).replace(/[^\d+]/g, '');
  // If it starts with 0 and looks like a TZ number, convert to +255
  if (/^0\d{9}$/.test(digits)) return `+255${digits.slice(1)}`;
  if (digits.startsWith('+')) return digits;
  if (/^255/.test(digits)) return `+${digits}`;
  return digits;
}

function SendMessageButton({ record, template }) {
  const [msg, setMsg] = useState(template(record));
  const phone = normalizePhone(record.owner_phone);
  const disabled = !phone;

  const openWhatsApp = () => {
    if (!phone) { toast.error('No phone number available'); return; }
    const url = `https://wa.me/${phone.replace('+', '')}?text=${encodeURIComponent(msg)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const openSMS = () => {
    if (!phone) { toast.error('No phone number available'); return; }
    // sms: URI opens the default SMS app on mobile; on desktop it may not do anything
    const url = `sms:${phone}?body=${encodeURIComponent(msg)}`;
    window.location.href = url;
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          disabled={disabled}
          className="gap-1.5"
          title={disabled ? 'No phone number on record' : 'Send message'}
          data-testid={`send-msg-${record.inspection_id}`}
        >
          <Send className="h-3.5 w-3.5" /> Send Message
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-96 p-4" side="left" align="start">
        <div className="space-y-3">
          <div>
            <p className="font-semibold text-sm">Notify {record.owner_name || 'customer'}</p>
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <Phone className="h-3 w-3" /> {phone || 'No number'}
            </p>
          </div>
          <Textarea
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            rows={5}
            className="text-sm resize-none"
            data-testid="send-msg-textarea"
          />
          <p className="text-xs text-muted-foreground">You can edit the message before sending.</p>
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={openWhatsApp}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              size="sm"
              data-testid="send-whatsapp"
            >
              <MessageCircle className="h-4 w-4 mr-1.5" /> WhatsApp
            </Button>
            <Button
              onClick={openSMS}
              variant="outline"
              size="sm"
              data-testid="send-sms"
            >
              <Phone className="h-4 w-4 mr-1.5" /> SMS
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * Full-featured expiry-forecast filtered list dialog.
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - window: 'expired' | '7' | '14' | '30'
 */
export default function ExpiryForecastDialog({ open, onClose, window }) {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [exporting, setExporting] = useState(false);
  const meta = WINDOW_META[window] || WINDOW_META['30'];

  const fetchRows = useCallback(async () => {
    if (!open || !window) return;
    setLoading(true);
    try {
      const res = await dashboardAPI.getExpiringInspections(window);
      setRows(res.data.records || []);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load expiry list');
    } finally {
      setLoading(false);
    }
  }, [open, window]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const res = await dashboardAPI.exportExpiringInspections(window, fmt);
      const blob = new Blob([res.data], {
        type: fmt === 'excel'
          ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          : 'application/pdf',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `expiring-${window}-${new Date().toISOString().slice(0, 10)}.${fmt === 'excel' ? 'xlsx' : 'pdf'}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      console.error(err);
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] flex flex-col p-0" data-testid="expiry-forecast-dialog">
        <DialogHeader className="p-6 pb-3 border-b">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className={`h-6 w-6 ${meta.color}`} />
              <div>
                <DialogTitle className="text-xl flex items-center gap-2">
                  {meta.title}
                  <Badge variant="secondary" data-testid="expiry-count-badge">{rows.length}</Badge>
                </DialogTitle>
                <DialogDescription>Filtered inspection records — send reminders or export the list</DialogDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleExport('excel')} disabled={exporting || rows.length === 0} data-testid="export-excel-btn">
                {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5 text-emerald-600" />}
                Excel
              </Button>
              <Button variant="outline" size="sm" onClick={() => handleExport('pdf')} disabled={exporting || rows.length === 0} data-testid="export-pdf-btn">
                {exporting ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <FileText className="h-4 w-4 mr-1.5 text-red-600" />}
                PDF
              </Button>
              <Button variant="ghost" size="icon" onClick={onClose}><X className="h-4 w-4" /></Button>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-auto p-6 pt-3">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <AlertTriangle className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No records found for this window.</p>
            </div>
          ) : (
            <div className={`rounded-lg border ${meta.accent}`}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Owner</TableHead>
                    <TableHead>Vehicle Reg / ID</TableHead>
                    <TableHead>Verification #</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Cylinders</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((r) => {
                    const identifier = r.registration_number || r.storage_serial_number || '—';
                    return (
                      <TableRow key={r.inspection_id} className="bg-white">
                        <TableCell className="font-medium">
                          <div className="truncate max-w-[180px]">{r.owner_name || '—'}</div>
                          {(r.make || r.model) && (
                            <div className="text-xs text-muted-foreground truncate max-w-[180px]">
                              {[r.make, r.model].filter(Boolean).join(' ')}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-mono text-sm">{identifier}</TableCell>
                        <TableCell className="font-mono text-xs">{r.verification_number || '—'}</TableCell>
                        <TableCell className="text-sm">{r.owner_phone || <span className="text-muted-foreground italic">no phone</span>}</TableCell>
                        <TableCell className="text-sm">
                          <Badge variant="outline" className={window === 'expired' ? 'bg-red-100 text-red-700 border-red-300' : ''}>
                            {formatDate(r.valid_to)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[160px]">
                          {r.cylinder_serials && r.cylinder_serials.length > 0 ? (
                            <span className="font-mono" title={r.cylinder_serials.join(', ')}>
                              {r.cylinder_serials.slice(0, 2).join(', ')}
                              {r.cylinder_serials.length > 2 ? ` +${r.cylinder_serials.length - 2}` : ''}
                            </span>
                          ) : <span className="text-muted-foreground">—</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <SendMessageButton record={r} template={meta.template} />
                            <Button asChild size="sm" variant="ghost" title="Open record">
                              <Link to={`/records/${r.record_id}`} target="_blank" rel="noopener noreferrer">
                                <ExternalLink className="h-4 w-4" />
                              </Link>
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
